/**
 * Per-channel WebSocket transport for the audio-transcription pipeline.
 *
 * Owns:
 *   - The WebSocket and its lifecycle (connect, reconnect with backoff,
 *     terminal close).
 *   - The pre-server-ready buffer (Deepgram drops PCM sent before the
 *     server confirms it's wired up, so we hold PCM in a bounded buffer
 *     and flush it on `onServerReady`).
 *   - The server-ready handshake fallback timer (force a fresh
 *     reconnect if the server never confirms).
 *   - The reconnect timer with exponential backoff (capped at 10s).
 *   - The wsSessionStartedAt anchor used to convert Deepgram session-
 *     relative offsets to absolute timestamps.
 *
 * Does NOT own:
 *   - Speaker-offset bookkeeping (that's Deepgram-session semantics
 *     used during message decoding — lives in AudioChannel).
 *   - Decoding TranscriptionMessage payloads — passed straight through
 *     to the delegate.
 *   - PCM production (lives in AudioPcmPipeline + the read loop).
 *
 * Lifecycle:
 *   - `connect()`           — opens the WS and resolves once it's open
 *                             (or once it errors). Subsequent calls
 *                             on an already-OPEN socket are no-ops.
 *   - `sendPcm(buffer)`     — sends if WS open AND server ready,
 *                             otherwise buffers (bounded by
 *                             MAX_BUFFER_BYTES). Returns whether the
 *                             buffer accepted the chunk and whether
 *                             it was sent immediately.
 *   - `sendControl(json)`   — sends a JSON control message if WS open;
 *                             returns whether it was sent.
 *   - `closeForReconnect()` — closes the WS; the close handler will
 *                             schedule a reconnect via the delegate.
 *   - `dispose()`           — final teardown; nulls handlers BEFORE
 *                             close so a late-firing onclose can't
 *                             schedule a zombie reconnect.
 */
import {
  MAX_BUFFER_BYTES,
  SERVER_READY_FALLBACK_MS,
  wireChannelName,
  type ChannelLabel,
  type TranscriptionMessage,
} from './types'
import * as Sentry from '@sentry/electron/renderer'

/** Initial reconnect delay (ms). Doubled (×1.5) on each subsequent
 *  failure up to RECONNECT_DELAY_MAX_MS. */
const RECONNECT_DELAY_INITIAL_MS = 1000
/** Cap on reconnect backoff so we don't end up with a 60s gap after a
 *  long outage. 10s is enough to avoid hammering the server while
 *  short enough to recover quickly. */
const RECONNECT_DELAY_MAX_MS = 10000
/** Browser WebSocket can occasionally remain stuck in CONNECTING without
 *  firing open/error/close. Bound that state so PCM buffering can recover via
 *  the normal reconnect path instead of growing until overflow. */
const CONNECT_TIMEOUT_MS = 10_000
/** Must match WS_SUBPROTOCOL in the gateway's middleware/auth.ts, which confirms it on the upgrade. */
const WS_SUBPROTOCOL = 'highlight-v1'

/** Information passed to the delegate when the transport finishes
 *  connecting (initial or reconnect). */
export interface TransportConnectInfo {
  /** True iff this is a reconnect (i.e. there was a prior disconnect). */
  isReconnect: boolean
  /** Wall-clock ms covered by the pending PCM buffer at the moment the
   *  WS opens. On reconnect this is `Date.now() - disconnectedAt`; on
   *  first connect (no prior disconnect) it's `Date.now() -
   *  firstBufferedAt`, i.e. the time the producer spent waiting for the
   *  initial WS handshake. Zero when no PCM was buffered. Combined with
   *  `bufferOverflowed`, lets the delegate emit a gap marker even when
   *  the very first connect attempt was slow enough to drop bytes. */
  gapMs: number
  /** True iff the pending PCM buffer hit MAX_BUFFER_BYTES (i.e. some
   *  audio was dropped and a gap marker is warranted). */
  bufferOverflowed: boolean
  /** Wall-clock anchor for this Deepgram session — message offsets get
   *  added to this to produce absolute timestamps. */
  sessionStartedAt: number
}

/** Caller-provided callbacks. The delegate is invoked synchronously
 *  except where noted; the transport never holds delegate calls
 *  pending across `await` boundaries. */
export interface ChannelTransportDelegate {
  /** WS opened (initial or reconnect). Note: the pre-server-ready
   *  buffer is NOT yet flushed when this fires — wait for
   *  `onServerReady` for that. Used by the channel to bump speaker
   *  offsets and insert gap markers if needed. */
  onWsConnect(info: TransportConnectInfo): void
  /** WS closed (any reason). The transport will schedule a reconnect
   *  internally if appropriate; this delegate call is purely
   *  informational so the channel can bookkeep status. */
  onWsDisconnect(): void
  /** Inbound message of any type. Caller is responsible for decoding
   *  the payload type. */
  onWsMessage(msg: TranscriptionMessage): void
  /** Server confirmed Deepgram is live (status message contained
   *  `Transcription session started` or `deepgram-connected`). The
   *  pre-server-ready buffer was already flushed before this fires.
   *  Caller may use this to clear any "buffering" UI signal. */
  onServerReady(bytesFlushed: number, chunksFlushed: number): void
  /** Bytes were sent on the wire. Used for diagnostics accounting. */
  onBytesSent(bytes: number): void
  /** Bytes were appended to the pre-WS / pre-server-ready buffer. */
  onBytesBuffered(bytes: number): void
  /** A WS-level message was received (any type, before decoding). */
  onWsMessageReceived(): void
  /** WebSocket URL builder. Lets the orchestrator decide between the
   *  audio-sessions DO endpoint and the legacy `/transcribe-ws`
   *  endpoint without coupling the transport to either. */
  getWsUrl(): string
  /** Promise resolving to a fresh OAuth access token. Called once per
   *  connect attempt — the transport doesn't cache. */
  getAuthToken(): Promise<string>
  /** True iff the transport is allowed to schedule a reconnect right
   *  now. Used to short-circuit the reconnect path during
   *  service-level teardown without forcing the orchestrator to call
   *  `dispose()` synchronously. */
  isReconnectAllowed(): boolean
}

/** Snapshot of transport state for diagnostics. */
export interface TransportSnapshot {
  wsReadyState: number | null
  serverReady: boolean
  pcmBufferBytes: number
}

/** Cumulative-state snapshot used by support-bundle / debug-info copy
 *  actions. Separate from `TransportSnapshot` because the diag reporter
 *  only needs current readyState/buffer figures, while the debug-info
 *  surface wants reconnect history and timing anchors too. */
export interface TransportDebugSnapshot {
  wsReadyState: number | null
  serverReady: boolean
  pcmBufferBytes: number
  /** Number of reconnect attempts scheduled since this transport was
   *  created. Bumped each time `scheduleReconnect` actually arms a
   *  timer (i.e. ignoring no-ops when a timer was already pending). */
  reconnectAttempts: number
  /** Next reconnect back-off delay in ms. Useful to confirm exponential
   *  back-off is engaging without trawling logs. */
  reconnectDelayMs: number
  /** Wall-clock ms of the last close that triggered reconnect, or null
   *  if currently connected / never disconnected. */
  disconnectedAt: number | null
  /** Wall-clock ms anchor used to convert backend offsets → absolute
   *  timestamps for the current WS session. */
  wsSessionStartedAt: number
}

export class ChannelTransport {
  private ws: WebSocket | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectDelay = RECONNECT_DELAY_INITIAL_MS
  /** Cumulative count of reconnect attempts scheduled since construction.
   *  Surfaced via `inspectDebug()` for the "Copy Debug Info" support flow.
   *  Distinct from `reconnectDelay` (which resets on a successful connect)
   *  — this value never decreases. */
  private reconnectAttempts = 0
  private serverReady = false
  private serverReadyTimer: ReturnType<typeof setTimeout> | null = null
  private connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null
  private pcmBuffer: Array<ArrayBuffer> = []
  private pcmBufferBytes = 0
  private pcmBufferOverflowed = false
  private disconnectedAt: number | null = null
  /** Promise of an in-flight `connect()` call, used to dedupe
   *  concurrent invocations. Cleared once the WS resolves (open or
   *  errored) so the next caller can either share the now-open
   *  socket via the `readyState === OPEN` short-circuit or kick off
   *  a fresh attempt. */
  private connectInFlight: Promise<void> | null = null
  /** Wall-clock time when the first chunk in the current `pcmBuffer` was
   *  appended. Re-used as the session anchor on flush so that buffered
   *  audio gets stamped to its actual capture time, not to the post-
   *  reconnect wall clock. Without this, a 60s outage would show all
   *  buffered transcripts as occurring in the 60s *after* the reconnect
   *  (because Deepgram returns offsets [0..N] relative to when it
   *  received the burst, and we'd anchor those offsets to `Date.now()`
   *  in `onWsOpen`). Cleared on flush, dispose, and any code path that
   *  empties the buffer. */
  private firstBufferedAt: number | null = null
  private wsSessionStartedAt: number
  /** True once dispose() has been called. Guards every async resume
   *  point so a late connect / reconnect / timer fire can't bring the
   *  transport back to life. */
  private disposed = false
  /** True iff we've ever opened the WS. Used to distinguish "first
   *  connect" from "reconnect" without leaking that state to the
   *  delegate. */
  private hasEverConnected = false

  constructor(
    readonly label: ChannelLabel,
    private readonly delegate: ChannelTransportDelegate,
    /** Initial wsSessionStartedAt anchor. Replaced by the live
     *  Date.now() value on each successful connect. */
    initialSessionStartedAt: number,
  ) {
    this.wsSessionStartedAt = initialSessionStartedAt
  }

  /** Open the WebSocket. Resolves once the WS is OPEN or has errored;
   *  resolves immediately if already OPEN. Idempotent across
   *  concurrent invocations — the second caller awaits the first
   *  call's outcome instead of opening a duplicate socket. Errors
   *  are surfaced via `onWsDisconnect`/reconnect, never thrown. */
  async connect(): Promise<void> {
    if (this.disposed) return
    if (this.ws?.readyState === WebSocket.OPEN) return
    // Dedupe: if a connect is already mid-flight (e.g. read loop
    // start path + reconnect timer racing), share its outcome
    // instead of opening a parallel socket. The resolved promise is
    // cleared inside `runConnect()` so the next call can either
    // reuse the now-open socket or kick off a fresh attempt.
    if (this.connectInFlight) return this.connectInFlight
    const attempt = this.runConnect()
    this.connectInFlight = attempt
    try {
      await attempt
    } finally {
      if (this.connectInFlight === attempt) {
        this.connectInFlight = null
      }
    }
  }

  private async runConnect(): Promise<void> {
    let token: string
    try {
      token = await this.delegate.getAuthToken()
    } catch (err) {
      if (this.disposed) return
      // Capture the underlying err so the difference between a credential-
      // store failure, a transient network error, and a signed-out account
      // is visible in telemetry instead of all three collapsing to a generic
      // "will retry" log. Reconnect scheduling still happens — but a
      // persistent auth failure now leaves a Sentry breadcrumb per attempt
      // so the silent forever-retry is observable.
      console.error(`[AudioTranscriptionService:${this.label}] Failed to get auth token; will retry:`, err)
      Sentry.captureException(err instanceof Error ? err : new Error(`getAuthToken failed: ${String(err)}`), {
        tags: {
          component: 'audio-transcription-service',
          channel: wireChannelName(this.label),
          errorId: 'audio-ws-auth-token-failed',
        },
      })
      this.scheduleReconnect()
      return
    }
    if (this.disposed) return

    const wsUrl = this.delegate.getWsUrl()
    console.log(`[AudioTranscriptionService] connectChannelWebSocket: ${this.label} → ${wsUrl}`)

    return new Promise<void>((resolve) => {
      let resolved = false
      const settle = () => {
        if (!resolved) {
          this.clearConnectTimeout()
          resolved = true
          resolve()
        }
      }

      // The gateway confirms the fixed protocol so the handshake never echoes the token-bearing one.
      const ws = new WebSocket(wsUrl, [WS_SUBPROTOCOL, `dangerouslyProvideToken~${token}`])
      ws.binaryType = 'arraybuffer'
      this.ws = ws
      this.armConnectTimeout(ws, settle)

      ws.onopen = () => {
        if (this.disposed || this.ws !== ws) {
          this.detachAndCloseSilently(ws)
          settle()
          return
        }
        this.onWsOpen(ws)
        settle()
      }

      ws.onmessage = (ev) => {
        if (this.disposed || this.ws !== ws) return
        this.delegate.onWsMessageReceived()
        try {
          const msg: TranscriptionMessage = JSON.parse(ev.data as string)
          this.delegate.onWsMessage(msg)
          this.applyServerReadyFromMessage(msg)
        } catch (err) {
          // Tag the channel so a bad payload from one backend doesn't
          // look like a generic failure spread across both. Trim the
          // payload to avoid flooding the log with a multi-KB message.
          const raw = typeof ev.data === 'string' ? ev.data : '<binary>'
          const preview = raw.length > 256 ? `${raw.slice(0, 256)}…(${raw.length}B)` : raw
          console.error(`[AudioTranscriptionService:${this.label}] Bad WS message:`, err, `payload="${preview}"`)
        }
      }

      ws.onclose = (ev: CloseEvent) => {
        // Defense-in-depth: dispose() / closeForReconnect() now also nulls
        // this handler before close() so we shouldn't reach here on a
        // replaced transport, but a future caller that bypasses the
        // close path (e.g. an external close from the server) could
        // still hit this. Bail if we're disposed or no longer the
        // owning socket.
        if (this.disposed || this.ws !== ws) {
          settle()
          return
        }
        console.warn(
          `[AudioTranscriptionService:${this.label}] WebSocket closed — code=${ev.code} reason="${ev.reason}"`,
        )
        this.handleClose()
        settle()
      }

      ws.onerror = (ev) => {
        // The Event object from a WS error provides essentially no
        // detail in browsers (security-by-design), but logging
        // type/target.url/readyState makes it observable that an error
        // fired before onclose. We still resolve here — onclose drives
        // reconnect.
        const target = ev.target as WebSocket | null
        console.warn(
          `[AudioTranscriptionService:${this.label}] WebSocket error event — type=${ev.type} readyState=${target?.readyState ?? 'unknown'} url=${target?.url ?? 'unknown'}`,
        )
        settle()
      }
    })
  }

  private armConnectTimeout(ws: WebSocket, settle: () => void): void {
    this.clearConnectTimeout()
    this.connectTimeoutTimer = setTimeout(() => {
      this.connectTimeoutTimer = null
      if (this.disposed || this.ws !== ws || ws.readyState !== WebSocket.CONNECTING) {
        return
      }
      const err = new Error(`Audio WS connect timeout after ${CONNECT_TIMEOUT_MS}ms (${this.label})`)
      console.warn(`[AudioTranscriptionService] ${err.message}`)
      Sentry.captureException(err, {
        tags: {
          component: 'audio-transcription-service',
          channel: wireChannelName(this.label),
          errorId: 'audio-ws-connect-timeout',
        },
      })
      this.closeForReconnect('connect-timeout', 4000)
      settle()
    }, CONNECT_TIMEOUT_MS)
  }

  private clearConnectTimeout(): void {
    if (!this.connectTimeoutTimer) return
    clearTimeout(this.connectTimeoutTimer)
    this.connectTimeoutTimer = null
  }

  private onWsOpen(ws: WebSocket): void {
    // NOTE: backoff is reset in `markServerReady()`, NOT here. A
    // socket that opens but never receives the server-ready status
    // (e.g. proxy that accepts the WS but blocks the upstream
    // Deepgram handshake) would otherwise reset the backoff to 1s on
    // every cycle and busy-loop reconnects. Holding the reset until
    // the connection is actually usable means a half-broken upstream
    // gets backed off properly.

    const isReconnect = this.hasEverConnected
    // Anchor the gap to the start of the buffered window we're about
    // to flush. On reconnect that's the prior `disconnectedAt`; on a
    // slow first connect it's `firstBufferedAt` (the moment the
    // producer started piling PCM into the buffer because the WS
    // wasn't open yet). Without the first-connect fallback, an
    // initial-handshake stall long enough to overflow the buffer
    // would silently drop audio with no visible gap marker.
    const gapAnchor = this.disconnectedAt ?? this.firstBufferedAt
    const gapMs = gapAnchor != null ? Date.now() - gapAnchor : 0
    const bufferOverflowed = this.pcmBufferOverflowed
    this.disconnectedAt = null
    this.pcmBufferOverflowed = false
    this.hasEverConnected = true

    // New Deepgram session → offsets reset to 0. Anchor them to wall-
    // clock time so post-reconnect transcripts get correct absolute
    // timestamps.
    this.wsSessionStartedAt = Date.now()

    // Both paths take config from the URL query params; the config
    // message just signals the worker to call startTranscription() so
    // the Deepgram session is live before we flush buffered audio.
    this.safeSendString(ws, JSON.stringify({ type: 'config' }))

    console.log(`[AudioTranscriptionService] ${this.label} WebSocket connected`)
    this.delegate.onWsConnect({
      isReconnect,
      gapMs,
      bufferOverflowed,
      sessionStartedAt: this.wsSessionStartedAt,
    })

    // Buffer flush is deferred to onServerReady — sending PCM before
    // the server reports "Transcription session started" causes
    // Deepgram to drop the leading audio (the WS is open but Deepgram
    // isn't wired up yet).
    this.armServerReadyFallback()
  }

  private armServerReadyFallback(): void {
    if (this.serverReadyTimer) clearTimeout(this.serverReadyTimer)
    this.serverReadyTimer = setTimeout(() => {
      this.serverReadyTimer = null
      if (this.disposed || this.serverReady) return
      // Server never confirmed Deepgram is wired up. Flushing anyway
      // would silently drop all subsequent audio (serverReady would
      // stick true on a half-broken socket). Force a fresh handshake
      // by closing the WS; the onclose handler schedules a reconnect
      // with backoff.
      const err = new Error(
        `Audio WS server-ready not received within ${SERVER_READY_FALLBACK_MS}ms (${this.label}) — forcing reconnect`,
      )
      console.warn(`[AudioTranscriptionService] ${err.message}`)
      Sentry.captureException(err, {
        // Use wire-name spelling (`mic` / `system`) for Sentry tag consistency.
        tags: { component: 'audio-transcription-service', channel: wireChannelName(this.label) },
      })
      this.closeForReconnect('server-ready-timeout', 4000)
    }, SERVER_READY_FALLBACK_MS)
  }

  private applyServerReadyFromMessage(msg: TranscriptionMessage): void {
    if (msg.type !== 'status') return
    const statusText = msg.status || msg.message || ''
    // Two backends produce this signal: the legacy native ASR worker
    // sends "Transcription session started"; the audio-sessions DO at
    // /api/v1/audio-sessions/:id/ws sends "deepgram-connected".
    if (statusText.includes('Transcription session started') || statusText.includes('deepgram-connected')) {
      this.markServerReady()
    }
  }

  /** Server confirmed Deepgram is live and ready to receive audio.
   *  Flush any PCM that accumulated during the WS-open → server-ready
   *  window so the first second or two isn't lost. */
  private markServerReady(): void {
    if (this.serverReady || this.disposed) return
    this.serverReady = true
    if (this.serverReadyTimer) {
      clearTimeout(this.serverReadyTimer)
      this.serverReadyTimer = null
    }
    // The connection is now actually usable — reset backoff so a
    // future disconnect from a healthy session retries quickly. If
    // the WS opened but never reached server-ready, this reset
    // never runs and the backoff keeps growing (see comment in
    // onWsOpen).
    this.reconnectDelay = RECONNECT_DELAY_INITIAL_MS

    let flushedBytes = 0
    let flushedChunks = 0
    if (this.pcmBuffer.length > 0) {
      // Anchor flushed audio to its capture time, not the reconnect time,
      // so Deepgram offsets map back to the actual buffered window. If the
      // buffer overflowed, the visible gap marker is the source of truth for
      // the dropped interval; the flushed stream remains contiguous.
      if (this.firstBufferedAt !== null) {
        const driftMs = Date.now() - this.firstBufferedAt
        if (driftMs > 1000) {
          console.log(
            `[AudioTranscriptionService] ${this.label} re-anchoring session to first buffered chunk: ${driftMs}ms before reconnect`,
          )
        }
        this.wsSessionStartedAt = this.firstBufferedAt
      }
      console.log(
        `[AudioTranscriptionService] ${this.label} server ready — flushing ${this.pcmBuffer.length} buffered audio chunks (${(this.pcmBufferBytes / 1024).toFixed(0)}KB)`,
      )
      const ws = this.ws
      if (ws?.readyState === WebSocket.OPEN) {
        // Concatenate all buffered chunks into a single ArrayBuffer
        // and send as one frame. Deepgram doesn't care about chunk
        // boundaries (the WS just cooks PCM into the decoder), and
        // the worst case (~MAX_BUFFER_BYTES = 3.84 MB across ~12k
        // chunks) collapses 12k synchronous ws.send() calls into one
        // — keeping a long-buffer flush from briefly blocking the
        // event loop on recovery. We still record the whole flush
        // through the delegate as one bytes-sent event, which keeps
        // diagnostics honest.
        const merged = this.concatBuffers(this.pcmBuffer, this.pcmBufferBytes)
        if (this.safeSendBuffer(ws, merged)) {
          flushedBytes = merged.byteLength
          flushedChunks = this.pcmBuffer.length
          this.delegate.onBytesSent(flushedBytes)
          // Only clear the buffer on successful flush. If send fails
          // we keep the bytes so the next reconnect's markServerReady
          // can retry — avoids silent dataloss when the WS state flips
          // between our OPEN check and the actual send().
          this.pcmBuffer = []
          this.pcmBufferBytes = 0
          this.firstBufferedAt = null
        } else {
          // Send failed at the WS layer (state flipped between our
          // OPEN check and the send). Preserve the buffer so the next
          // reconnect cycle can retry the flush; mark overflow so the
          // delegate's gap marker fires if we keep failing and the
          // buffer eventually exceeds MAX_BUFFER_BYTES via sendPcm.
          // Without preservation this was a silent dataloss path —
          // serverReady was already true so no further onWsConnect
          // would re-flush the lost bytes.
          const sizeKb = (this.pcmBufferBytes / 1024).toFixed(0)
          console.warn(
            `[AudioTranscriptionService:${this.label}] flush send failed mid-handshake — ${sizeKb}KB retained for next reconnect`,
          )
          Sentry.captureException(
            new Error(`audio flush send failed mid-handshake (${this.label}, ${sizeKb}KB retained)`),
            {
              tags: {
                component: 'audio-transcription-service',
                channel: wireChannelName(this.label),
                errorId: 'audio-ws-flush-send-failed',
              },
            },
          )
        }
      } else {
        // No usable socket at flush time — keep the buffer for next reconnect.
        // Same rationale as the failed-send branch: clearing here would silently
        // drop audio the user expects to see in the transcript.
        console.warn(
          `[AudioTranscriptionService:${this.label}] server-ready arrived with no open WS — buffer retained for next reconnect`,
        )
      }
    }
    this.delegate.onServerReady(flushedBytes, flushedChunks)
  }

  /** Copy a list of ArrayBuffers into one contiguous ArrayBuffer.
   *  Caller passes `totalBytes` so we don't iterate twice. */
  private concatBuffers(chunks: Array<ArrayBuffer>, totalBytes: number): ArrayBuffer {
    const out = new Uint8Array(totalBytes)
    let offset = 0
    for (const chunk of chunks) {
      out.set(new Uint8Array(chunk), offset)
      offset += chunk.byteLength
    }
    return out.buffer
  }

  /** Push a PCM chunk. Returns whether it was sent immediately, buffered,
   *  or dropped due to buffer cap. The transport accounts for sent /
   *  buffered bytes via the delegate; the orchestrator never has to
   *  call recordBytesSent / recordBytesBuffered itself. */
  sendPcm(pcm: ArrayBuffer): { sent: boolean; buffered: boolean; dropped: boolean } {
    if (this.disposed) return { sent: false, buffered: false, dropped: true }

    const ws = this.ws
    if (ws?.readyState === WebSocket.OPEN && this.serverReady) {
      // Try the live send first. If it fails (state flipped between
      // our OPEN check and the actual ws.send — happens on flaky
      // networks where the OS has marked the socket dead but
      // Chromium hasn't yet fired onclose), fall through to the
      // buffer path so the audio survives the impending reconnect
      // instead of going to /dev/null. Without this fall-through,
      // diagnostics over-counts `bytesSent` AND we silently lose
      // the chunk.
      if (this.safeSendBuffer(ws, pcm)) {
        this.delegate.onBytesSent(pcm.byteLength)
        return { sent: true, buffered: false, dropped: false }
      }
    }

    if (this.pcmBufferBytes >= MAX_BUFFER_BYTES) {
      // One-shot loud log on the transition into overflow so the drop
      // path isn't silent. Steady-state overflow (a chunk dropped every
      // 20ms while the WS is down) would otherwise flood the console;
      // the overlay's `Events` card surfaces the gap-marker that fires
      // on the next reconnect for the ongoing case.
      if (!this.pcmBufferOverflowed) {
        console.warn(
          `[AudioTranscriptionService:${this.label}] PCM buffer overflowed at ${(this.pcmBufferBytes / 1024).toFixed(0)}KB — dropping new chunks until reconnect`,
        )
      }
      this.pcmBufferOverflowed = true
      return { sent: false, buffered: false, dropped: true }
    }

    // First chunk in a new buffering window — record when it was
    // appended so markServerReady() can anchor the session to it.
    if (this.pcmBuffer.length === 0 && this.firstBufferedAt === null) {
      this.firstBufferedAt = Date.now()
    }
    this.pcmBuffer.push(pcm)
    this.pcmBufferBytes += pcm.byteLength
    this.delegate.onBytesBuffered(pcm.byteLength)
    return { sent: false, buffered: true, dropped: false }
  }

  /** Current bytes held in the pre-server-ready / disconnect buffer.
   *  Surfaced so the orchestrator can publish a "buffered Xs" UI
   *  signal during reconnects without poking at internal fields. */
  getBufferedBytes(): number {
    return this.pcmBufferBytes
  }

  /** Send a JSON control message (e.g. force_segment). No-op if the WS
   *  isn't OPEN. Returns true iff the send was attempted. */
  sendControl(message: object): boolean {
    if (this.disposed || this.ws?.readyState !== WebSocket.OPEN) return false
    this.safeSendString(this.ws, JSON.stringify(message))
    return true
  }

  /** Close the WS to force a reconnect. The close handler will mark
   *  this transport disconnected and schedule the reconnect. Used by
   *  e.g. the "Transcription session stopped" status path. */
  closeForReconnect(reason: string, code = 1000): void {
    if (this.disposed) return
    const ws = this.ws
    if (!ws) {
      // No WS to close; if we were ever connected, just schedule.
      if (this.hasEverConnected) this.scheduleReconnect()
      return
    }
    this.ws = null
    // Detach handlers BEFORE close. Without this, an immediate restart
    // path could replace this transport while the async onclose is
    // pending — the handler would then run on a "not the live socket
    // anymore" state and we'd schedule a phantom reconnect. We
    // explicitly drive reconnect here instead.
    ws.onopen = null
    ws.onmessage = null
    ws.onerror = null
    ws.onclose = null
    try {
      ws.close(code, reason)
    } catch {
      // ignore close errors
    }
    this.handleClose()
  }

  private handleClose(): void {
    this.clearConnectTimeout()
    this.ws = null
    this.serverReady = false
    if (this.serverReadyTimer) {
      clearTimeout(this.serverReadyTimer)
      this.serverReadyTimer = null
    }
    this.disconnectedAt = Date.now()
    this.delegate.onWsDisconnect()
    if (this.delegate.isReconnectAllowed()) {
      this.scheduleReconnect()
    }
  }

  private scheduleReconnect(): void {
    if (this.disposed) return
    if (this.reconnectTimer) return
    if (!this.delegate.isReconnectAllowed()) return
    this.reconnectAttempts += 1
    const delay = this.reconnectDelay
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      if (this.disposed) return
      if (!this.delegate.isReconnectAllowed()) return
      void this.connect().catch((err) => {
        console.error(`[AudioTranscriptionService:${this.label}] Reconnect attempt failed:`, err)
      })
    }, delay)
    this.reconnectDelay = Math.min(this.reconnectDelay * 1.5, RECONNECT_DELAY_MAX_MS)
  }

  /** Final teardown. Cancels the reconnect timer, the server-ready
   *  fallback, and detaches all WS handlers BEFORE close so a late-
   *  firing onclose can't reschedule a reconnect onto a disposed
   *  transport. Idempotent. */
  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.serverReadyTimer) {
      clearTimeout(this.serverReadyTimer)
      this.serverReadyTimer = null
    }
    this.clearConnectTimeout()
    if (this.ws) {
      this.detachAndCloseSilently(this.ws)
      this.ws = null
    }
    this.pcmBuffer = []
    this.pcmBufferBytes = 0
    this.firstBufferedAt = null
    // Drop the in-flight connect promise so the awaiting caller's
    // catch path doesn't keep a closed-over reference to a disposed
    // transport. The promise itself resolves naturally via the
    // settle() in runConnect once the WS errors / closes.
    this.connectInFlight = null
  }

  private detachAndCloseSilently(ws: WebSocket): void {
    ws.onopen = null
    ws.onmessage = null
    ws.onerror = null
    ws.onclose = null
    try {
      ws.close(1000, 'stopped')
    } catch {
      // ignore close errors
    }
  }

  /** Send a binary frame, returning true on success and false (with a
   *  warn log) on failure. Used by the live and flush paths so they
   *  can decide whether to count bytes / fall through to buffering. */
  private safeSendBuffer(ws: WebSocket, buffer: ArrayBuffer): boolean {
    try {
      ws.send(buffer)
      return true
    } catch (err) {
      console.warn(`[AudioTranscriptionService:${this.label}] WS send (binary) failed:`, err)
      return false
    }
  }

  private safeSendString(ws: WebSocket, payload: string): void {
    try {
      ws.send(payload)
    } catch (err) {
      console.warn(`[AudioTranscriptionService:${this.label}] WS send (text) failed:`, err)
    }
  }

  // ── Inspection ─────────────────────────────────────────────────

  isWsOpen(): boolean {
    return this.ws?.readyState === WebSocket.OPEN
  }

  isServerReady(): boolean {
    return this.serverReady
  }

  isReady(): boolean {
    return this.isWsOpen() && this.serverReady
  }

  getSessionStartedAt(): number {
    return this.wsSessionStartedAt
  }

  inspect(): TransportSnapshot {
    return {
      wsReadyState: this.ws?.readyState ?? null,
      serverReady: this.serverReady,
      pcmBufferBytes: this.pcmBufferBytes,
    }
  }

  inspectDebug(): TransportDebugSnapshot {
    return {
      wsReadyState: this.ws?.readyState ?? null,
      serverReady: this.serverReady,
      pcmBufferBytes: this.pcmBufferBytes,
      reconnectAttempts: this.reconnectAttempts,
      reconnectDelayMs: this.reconnectDelay,
      disconnectedAt: this.disconnectedAt,
      wsSessionStartedAt: this.wsSessionStartedAt,
    }
  }
}
